import { Component, AfterViewInit, ViewChild, ElementRef, NgZone, OnDestroy } from '@angular/core'
import { NeuroglancerViewer } from '../../shared/model/neuroglancer-type-mappings'
import { Subject } from 'rxjs'
import { ViewerService } from './viewer.service'

@Component({
    selector: 'app-neuroglancer-frame',
    templateUrl: './neuroglancer-frame.component.html',
    styleUrls: ['./neuroglancer-frame.component.scss'],
})
export class NeuroglancerFrameComponent implements OnDestroy, AfterViewInit {
    @ViewChild('iframe') iframe?: ElementRef<HTMLIFrameElement>

    private ngUnsubscribe = new Subject<void>()
    initialized = false

    constructor(private readonly zone: NgZone, private readonly viewerService: ViewerService) {}

    ngOnDestroy(): void {
        this.ngUnsubscribe.next()
        this.ngUnsubscribe.complete()
    }

    ngAfterViewInit(): void {
        if (this.iframe == null) {
            throw new Error('Viewer iframe not found')
        }
        const iframeWindow = this.iframe.nativeElement.contentWindow!
        iframeWindow.addEventListener('DOMContentLoaded', () => this.onDomLoaded(iframeWindow))
    }

    private onDomLoaded(iframeWindow: Window): void {
        const doc = iframeWindow.document

        const link = doc.createElement('link')
        link.type = 'text/css'
        link.rel = 'stylesheet'
        link.href = '../neuroglancer-extra-styles.css'
        doc.head.appendChild(link)

        // enable our custom styles
        doc.body.classList.add('ng-customized')

        // neuroglancer calls setupDefaultViewer() on the DOMContentLoaded event,
        // (same that triggers this code), so we can now queue our customizations
        setTimeout(() => {
            const viewer = (<any>iframeWindow).viewer
            if (viewer == null) {
                throw new Error('Viewer missing in iframe, cannot initialize.')
            }
            this.onDefaultViewerLoaded(viewer)
        }, 300)
    }

    private onDefaultViewerLoaded(viewer: NeuroglancerViewer): void {
        // Note: This is neuroglancer setup code and it runs outside of the
        // Angular zone. Which is good for neuroglancer's performance. Event
        // handlers we install here will be invoked outside the zone too, and
        // will not automatically trigger angular change detection when done.
        //
        // console.log('inAngularZone:', NgZone.isInAngularZone())

        // disable menu bar, etc (not "all UI", but close; we fix the rest with CSS)
        viewer.uiConfiguration.showUIControls.value = false
        // list of layers at the top: (already disabled by the above)
        // viewer.uiConfiguration.showLayerPanel.value = false

        // black/white "view selected" border (indicates keyboard input)
        viewer.uiConfiguration.showPanelBorders.value = false

        viewer.layerManager.layersChanged.add(() => {
            // console.log('Layers have Changed!')
            // this is called from the iframe, trigger Angular change detection:
            this.zone.run(() => {})
        })

        // For hover events
        //
        // Actually, this is only needed if we directly access the neuroglancer values
        // from a template. I don't think we do that any more, we use the trackableService.
        /*
        viewer.layerSelectedValues.changed.add(() => {
            // console.log('layerSelectedValues changed:', viewer.layerSelectedValues.toJSON());
            // Performance notes:
            //
            // There are a lot of those events, we probably want to combine/debounce
            // before zone.run(). Though it seems okay so far...
            //
            // Re-fetching all hover values on every angular change detection event
            // (as we currently do) may become problematic. Using a (debounced?)
            // observable here may help somewhat..
            //
            this.zone.run(() => {})
        })
        */

        // This is triggered e.g. if the "segments" list (selected segments) changes,
        // could be used to observe the selection. (But also triggers on layout change.)
        // viewer.layerSpecification.changed.add(() => {
        //   // console.log('layer spec change:', viewer.layerSpecification.toJSON());
        //   this.zone.run(() => {})
        // });

        this.customizeKeyBindings(viewer)
        this.viewerService.setViewer(viewer)
        this.initialized = true
    }

    private customizeKeyBindings(viewer: NeuroglancerViewer): void {
        // Tentative Goal: (PoC)
        // - prevent layout changes (e.g. spacebar key)
        // - prevent rotation (hard to undo)
        // Unknown:
        // - what we really want (e.g. double-click select?)

        const globalBindings = viewer.inputEventBindings.global
        const sliceViewBindings = viewer.inputEventBindings.sliceView
        const perspectiveViewBindings = viewer.inputEventBindings.perspectiveView

        // attempt at black-listing individual key bindings:
        // for (const [name, action] of sliceViewBindings.entries()) {
        //     if (action.action.includes('rotate')) {
        //         console.log(name, '/', action.originalEventIdentifier, '==>', action.action)

        // console.log('get:', sliceViewBindings.get(name))  // this works
        // Not sure what the problem is here:
        // sliceViewBindings.delete(name)  // no effect
        // sliceViewBindings.delete(action.originalEventIdentifier!)  // no effect

        // }
        // }

        // This works. Removes all interactions, including mouse.
        sliceViewBindings.clear()
        // This works. But it's copied from neuroglancer source (ugly).
        sliceViewBindings.setFromObject({
            'at:mousedown0': { action: 'translate-via-mouse-drag', stopPropagation: true },
            'control+wheel': { action: 'zoom-via-wheel', preventDefault: true },
            'at:wheel': { action: 'z+1-via-wheel', preventDefault: true },
            'at:mousedown2': 'move-to-mouse-position',
            'at:touchpinch': 'zoom-via-touchpinch',
            'at:touchhold1': 'move-to-mouse-position',
            'at:touchtranslate1': 'translate-z-via-touchtranslate',
            'at:touchtranslate2': 'translate-in-plane-via-touchtranslate',
            'keyl': 'recolor',
        })
        globalBindings.clear()
        perspectiveViewBindings.clear()
    }
}
